import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, X, CalendarCheck } from "lucide-react";
import { openBooking } from "../lib/booking";
import { askLink } from "../lib/whatsapp";
import WhatsAppIcon from "./WhatsAppIcon";
import Hud from "./HudOverlay";

const GET = [
  "Jurisdiction, structure & licence recommendation for your exact model",
  "A realistic all-in budget — our fees and every third-party cost, itemized",
  "Platform, liquidity and payments stack mapped to your budget",
  "A written launch map delivered after the call — yours to keep",
];

const FOR = [
  "Founders planning to launch within the next 6–12 months",
  "Owners of communities, IB books or audiences who want their own brand",
  "Operating brokers migrating platform, licence or providers",
  "Smaller founders who want a staged, budget-realistic route",
];

const NOT_FOR = [
  "Exploring the idea with no budget set aside yet",
  "Looking for guaranteed profits or a passive investment",
  "Only after free provider price lists, with no launch plan",
  "Businesses outside trading, brokerage or prop firms",
];

const AFTER_PAY = [
  { t: "Pay & pick your slot", d: "Secure Stripe checkout, then choose a 30-minute time that suits you." },
  { t: "Receipt + calendar invite", d: "Your confirmation and invitation arrive by email within minutes." },
  { t: "Call + written launch map", d: "We talk, then you receive your launch map and budget in writing." },
];

const StrategyCall = () => {
  return (
  <section id="strategy-call" className="relative py-12 sm:py-16 overflow-hidden glow-top border-t border-white/[0.06]" data-testid="strategy-call-section">
    <div className="absolute inset-0 bg-[#081024]/70" />
    <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(229,197,111,0.10) 0%, transparent 70%)" }} />

    <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
      <div className="flex items-center gap-3 text-[11px] tracking-[0.5em] text-purple-400 uppercase">
        <span className="w-8 h-px bg-purple-500/60 neon-dash" />
        The first step
      </div>
      <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-zinc-100 leading-[1.05] tracking-tight max-w-3xl">
        One call replaces weeks of <em className="not-italic gold-text">guessing.</em>
      </h2>
      <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed max-w-2xl">
        The $250 Strategy Call is 30 minutes with the team that has launched brokerages
        and prop firms since 2015 — and the full fee is credited toward any engagement
        if you proceed.
      </p>

      <div className="mt-9 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* What you get */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="lux-card relative overflow-hidden rounded-2xl border border-[#E5C56F]/30 p-6 sm:p-8"
          data-testid="strategy-call-what-you-get"
        >
          <Hud />
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#E5C56F]">In 30 minutes you get</div>
          <ul className="mt-5 space-y-3.5">
            {GET.map((g) => (
              <li key={g} className="flex items-start gap-2.5 text-sm text-zinc-200">
                <Check size={15} strokeWidth={2} className="mt-0.5 text-[#E5C56F] shrink-0" />
                {g}
              </li>
            ))}
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openBooking("strategy-section-pay")}
              className="inline-flex items-center gap-2.5 rounded-full bg-gradient-to-r from-[#E5C56F] to-[#C9A64B] px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#1a1405] hover:brightness-110 hover:-translate-y-0.5 transition-all shadow-[0_0_28px_rgba(229,197,111,0.25)]"
              data-testid="strategy-call-pay-btn"
            >
              <CalendarCheck size={15} />
              Book a $250 Strategy Call
            </button>
            <a
              href={askLink("the $250 Strategy Call")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-purple-600/10 px-6 py-3 text-[12px] uppercase tracking-[0.2em] text-purple-200 hover:border-purple-400/60 hover:bg-purple-600/20 hover:-translate-y-0.5 transition-all"
              data-testid="strategy-call-ask-btn"
            >
              <WhatsAppIcon size={15} />
              Ask a question
            </a>
            <Link
              to="/incorporation"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-purple-300 hover:text-purple-200 transition-colors"
              data-testid="strategy-call-incorporation-link"
            >
              View Incorporation Options →
            </Link>
          </div>
        </motion.div>

        {/* For / not for */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.75, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          className="lux-card relative overflow-hidden rounded-2xl p-6 sm:p-8"
          data-testid="strategy-call-who-for"
        >
          <Hud />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-purple-300">Book it if you are</div>
              <ul className="mt-5 space-y-3.5">
                {FOR.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-[13px] text-zinc-200">
                    <Check size={14} strokeWidth={2} className="mt-0.5 text-purple-300 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500">Not the right fit if</div>
              <ul className="mt-5 space-y-3.5">
                {NOT_FOR.map((n) => (
                  <li key={n} className="flex items-start gap-2.5 text-[13px] text-zinc-400">
                    <X size={14} strokeWidth={2} className="mt-0.5 text-zinc-600 shrink-0" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 text-[12px] text-zinc-400 leading-relaxed">
            The $250 is credited in full toward any engagement — the call effectively costs nothing if you launch with us.
          </div>
        </motion.div>
      </div>

      <motion.ol
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
        data-testid="after-pay-timeline"
      >
        {AFTER_PAY.map((st, i) => (
          <li key={st.t} className="relative rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4 flex items-start gap-3.5">
            <span className="shrink-0 w-7 h-7 rounded-full border border-[#E5C56F]/40 bg-[#E5C56F]/10 flex items-center justify-center text-[11px] font-semibold gold-text">{i + 1}</span>
            <div>
              <div className="text-[12px] text-zinc-100 font-medium">{st.t}</div>
              <div className="mt-1 text-[11px] text-zinc-400 leading-relaxed">{st.d}</div>
            </div>
          </li>
        ))}
      </motion.ol>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] px-6 py-5 flex flex-wrap items-center gap-x-6 gap-y-3"
        data-testid="starter-founders-strip"
      >
        <div className="text-sm text-zinc-200">
          <span className="gold-text font-semibold">Starting with a smaller budget?</span>{" "}
          Incorporation from $6,500 · prop firm from $18,000 — staged so you only buy what your launch needs.
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12px] uppercase tracking-[0.18em]">
          <Link to="/insights/prop-firm-regulation-2026-update" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-prop-reg-2026">Prop firm regulation 2026 →</Link>
          <Link to="/insights/seychelles-license-2026-changes" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-seychelles-2026">Seychelles licence changes 2026 →</Link>
          <Link to="/insights/start-brokerage-2026-launch-plan" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-90-day-plan">90-day brokerage launch plan →</Link>
          <Link to="/insights/start-brokerage-south-africa-small-budget" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-sa">South Africa on a budget →</Link>
          <Link to="/insights/start-brokerage-asia-small-budget" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-asia">The Asia playbook →</Link>
          <Link to="/brokerage-setup-south-africa" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-sa-page">Launch from South Africa →</Link>
          <Link to="/brokerage-setup-asia" className="text-purple-300 hover:text-purple-200 transition-colors" data-testid="starter-link-asia-page">Launch from Asia →</Link>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

export default StrategyCall;
